<style scoped>
.photo-thumbnail {
	width: 100px;
	height: 100px;
	object-fit: cover;
	cursor: pointer;
	border-radius: 4px;
	transition: transform 0.2s;
}
.photo-thumbnail:hover {
	transform: scale(1.05);
}
</style>
<template>
	<div v-if="inspectionEvaluating">
		<p>loading inspection {{ inspectionURI }}...</p>
	</div>
	<div v-else-if="!inspection" class="details-section p-3 border-top">
		<p class="text-muted mb-0">
			<i class="bi bi-mosquito me-1"></i> Inspection not found
		</p>
	</div>
	<div v-else class="details-section p-3 border-top">
		<div class="d-flex justify-content-between align-items-start mb-3">
			<div>
				<h5 class="mb-1"><i class="bi bi-mosquito me-1"></i> Mosquito Check</h5>
				<small class="text-muted">Inspection #{{ inspection.id }}</small>
			</div>
			<span class="badge bg-secondary">
				<TimeRelativeWithTooltip :time="inspection.created" />
			</span>
		</div>

		<div class="d-flex align-items-center gap-2 mb-3">
			<span :class="statusBadgeClass(inspection.status)">
				{{ inspection.status }}
			</span>
			<span class="badge bg-light text-dark border">
				{{ inspection.language == "es" ? "ES" : "EN" }}
			</span>
			<template v-if="inspection.has_link">
				<span
					v-if="inspection.link_clicks > 0"
					class="badge bg-success"
					title="Resident clicked the Mosquito Check link"
				>
					<i class="bi bi-link-45deg me-1"></i> Link clicked
					{{ inspection.link_clicks }}x
					<span
						v-if="inspection.link_last_clicked_at"
						class="badge bg-success border ms-1"
					>
						<TimeRelativeWithTooltip :time="inspection.link_last_clicked_at" />
					</span>
				</span>
				<span v-else class="badge bg-secondary">
					<i class="bi bi-link-45deg me-1"></i> Link not clicked
				</span>
			</template>
		</div>

		<div class="d-flex gap-3 small text-muted mb-3">
			<span
				><span class="fw-medium text-success">{{ confirmedCount }}</span>
				confirmed</span
			>
			<span
				><span class="fw-medium">{{ clearCount }}</span> clear</span
			>
			<span v-if="inspection.off_property.length > 0"
				><span class="fw-medium">{{ inspection.off_property.length }}</span>
				beyond property</span
			>
		</div>

		<!-- Contact -->
		<div class="card mb-3">
			<div class="card-body">
				<h6 class="text-muted small mb-2">
					<i class="bi bi-person me-1"></i> Contact
				</h6>
				<div v-if="inspection.contact">
					<div class="fw-medium">
						{{ inspection.contact.name || "not given" }}
					</div>
					<div v-if="inspection.contact.emails.length > 0" class="small">
						<i class="bi bi-envelope me-1"></i>
						<a
							v-for="email in inspection.contact.emails"
							:key="email"
							:href="'mailto:' + email"
							>{{ email }}</a
						>
					</div>
					<div v-if="inspection.contact.phones.length > 0" class="small">
						<i class="bi bi-phone me-1"></i>
						<a
							v-for="phone in inspection.contact.phones"
							:key="phone.e164"
							:href="'tel:' + phone.e164"
							>{{ phone.e164 }}</a
						>
					</div>
				</div>
				<div v-else class="text-muted small">No contact on file</div>
				<div v-if="inspection.property_address" class="small mt-1">
					<i class="bi bi-geo-alt me-1"></i>
					{{ inspection.property_address }}
				</div>
			</div>
		</div>

		<!-- Confirmed habitats -->
		<div class="card mb-3">
			<div
				class="card-header d-flex justify-content-between align-items-center"
			>
				<span
					><i class="bi bi-clipboard-check me-1"></i> Confirmed Habitats</span
				>
				<span class="badge bg-primary">{{ confirmedCount }}</span>
			</div>
			<div class="card-body">
				<div
					v-if="confirmedObservations.length > 0"
					class="d-flex flex-column gap-3"
				>
					<div
						v-for="observation in confirmedObservations"
						:key="observation.habitat_key"
						class="border-bottom pb-2"
					>
						<div class="fw-medium">{{ habitatLabel(observation) }}</div>
						<div class="d-flex align-items-center gap-2 mt-1">
							<span :class="larvaeBadgeClass(observation.larvae_present)">
								{{ larvaeLabel(observation.larvae_present) }}
							</span>
							<span
								v-if="observation.already_fixed === true"
								class="badge bg-success"
							>
								<i class="bi bi-check-circle me-1"></i> Already fixed
							</span>
							<img
								v-for="photo in observation.photos"
								:key="photo.id"
								:src="`/api/image/${photo.uuid}/content`"
								class="photo-thumbnail ms-auto"
								@click="emit('viewImage', photo.uuid)"
								alt="Habitat photo"
							/>
						</div>
					</div>
				</div>
				<div v-else class="text-muted text-center py-3">
					<i class="bi bi-clipboard-x fs-4"></i>
					<p class="mb-0 small">No confirmed habitats</p>
				</div>
			</div>
		</div>

		<!-- Off-property reports -->
		<div class="card">
			<div
				class="card-header d-flex justify-content-between align-items-center"
			>
				<span><i class="bi bi-diagram-3 me-1"></i> Beyond Your Property</span>
				<span class="badge bg-primary">{{
					inspection.off_property.length
				}}</span>
			</div>
			<div class="card-body">
				<div
					v-if="inspection.off_property.length > 0"
					class="d-flex flex-column gap-3"
				>
					<div
						v-for="report in inspection.off_property"
						:key="report.category"
						class="border-bottom pb-2"
					>
						<span class="badge bg-secondary text-capitalize">{{
							report.category
						}}</span>
						<p v-if="report.description" class="mb-1 mt-1">
							{{ report.description }}
						</p>
						<img
							v-for="photo in report.photos"
							:key="photo.id"
							:src="`/api/image/${photo.uuid}/content`"
							class="photo-thumbnail me-1"
							@click="emit('viewImage', photo.uuid)"
							alt="Off-property photo"
						/>
					</div>
				</div>
				<div v-else class="text-muted text-center py-3">
					<i class="bi bi-camera-slash fs-4"></i>
					<p class="mb-0 small">No off-property reports</p>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { computedAsync } from "@vueuse/core";

import TimeRelativeWithTooltip from "@/components/TimeRelativeWithTooltip.vue";
import { habitatCopy } from "@/rmo/content/mosquito/habitatCopy";
import { useStoreResource } from "@/store/resource";
import type { VirtualInspectionDetail } from "@/type/api";

interface Emits {
	(e: "viewImage", uuid: string): void;
}
interface Props {
	inspectionURI: string;
}
const emit = defineEmits<Emits>();
const props = defineProps<Props>();
const storeResource = useStoreResource();

type DetailObservation = VirtualInspectionDetail["observations"][number];

const inspectionEvaluating = ref(false);
const inspection = computedAsync<VirtualInspectionDetail | undefined>(
	async () => {
		if (!props.inspectionURI) return undefined;
		try {
			const detail = await storeResource.virtualinspection.ensureURI(
				props.inspectionURI,
			);
			if (detail && Array.isArray(detail.observations)) {
				return detail;
			}
			// Cached copy is a List summary (no observations); fetch the full detail.
			return await storeResource.virtualinspection.fetchByURI(
				props.inspectionURI,
			);
		} catch {
			return undefined;
		}
	},
	undefined,
	inspectionEvaluating,
);

const confirmedObservations = computed<DetailObservation[]>(() => {
	return (inspection.value?.observations ?? []).filter((o) => o.present);
});
const confirmedCount = computed<number>(
	() => confirmedObservations.value.length,
);
const clearCount = computed<number>(() => {
	return (inspection.value?.observations ?? []).filter((o) => !o.present)
		.length;
});

function statusBadgeClass(status: string): string {
	switch (status) {
		case "submitted":
			return "badge bg-success";
		case "started":
			return "badge bg-info";
		default:
			return "badge bg-secondary";
	}
}
function larvaeLabel(value: boolean | null): string {
	if (value === true) return "Yes";
	if (value === false) return "No";
	return "Not sure";
}
function larvaeBadgeClass(value: boolean | null): string {
	if (value === true) return "badge bg-danger";
	if (value === false) return "badge bg-success";
	return "badge bg-secondary";
}
function habitatLabel(observation: DetailObservation): string {
	const copy = habitatCopy(observation.habitat_key);
	if (!copy) {
		return observation.habitat_key;
	}
	const { en, es } = copy.label;
	if (es && es != en) {
		return `${en} / ${es}`;
	}
	return en || observation.habitat_key;
}
</script>
